import {
  Alert,
  Box,
  Breakpoint,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material'
import { ReactNode } from 'react'
import { ConfirmationActions, ConfirmationProps } from './ConfirmationActions'

interface Props extends ConfirmationProps {
  children?: ReactNode
  open: boolean
  title: string
  blockClickOutside?: boolean
  message?: React.ReactNode
  warning?: string
  warningPosition?: 'top' | 'bottom'
  maxWidth?: Breakpoint
  fullScreen?: boolean
  hasTitleBorder?: boolean
}

export const ConfirmationDialog = ({
  children,
  open,
  title,
  blockClickOutside,
  message,
  warning,
  warningPosition = 'top',
  maxWidth,
  fullScreen,
  isLoading,
  formId,
  hidePrimaryAction,
  hideSecondaryAction,
  primaryActionText,
  primaryActionDisabled,
  primaryActionDataCy,
  secondaryActionText,
  secondaryActionColor,
  secondaryActionDisabled,
  secondaryType,
  closeLabel,
  mobile,
  hasBorderTop,
  rightRenderOption,
  hasTitleBorder,
  onClose,
  onCancel,
  onConfirm,
  onSecondaryAction,
}: Props) => {
  return (
    <Dialog
      onClose={!blockClickOutside ? onClose : undefined}
      open={open}
      fullWidth={!!maxWidth}
      maxWidth={maxWidth ?? 'md'}
      fullScreen={fullScreen}
    >
      <DialogTitle
        sx={
          hasTitleBorder
            ? { borderBottom: '1px solid', borderColor: 'divider', pb: '12px', mb: '12px' }
            : undefined
        }
      >
        {title}
      </DialogTitle>

      <DialogContent>
        {!!warning && warningPosition === 'top' && (
          <Box mb={2}>
            <Alert severity='warning'>{warning}</Alert>
          </Box>
        )}
        {!!message && (
          <DialogContentText>
            <Typography component='span'>{message}</Typography>
          </DialogContentText>
        )}
        <Box>{children}</Box>
        {!!warning && warningPosition === 'bottom' && (
          <Box mb={2}>
            <Alert severity='warning'>{warning}</Alert>
          </Box>
        )}
      </DialogContent>

      <ConfirmationActions
        isLoading={isLoading}
        formId={formId}
        hidePrimaryAction={hidePrimaryAction}
        hideSecondaryAction={hideSecondaryAction}
        primaryActionText={primaryActionText}
        primaryActionDisabled={primaryActionDisabled}
        primaryActionDataCy={primaryActionDataCy}
        secondaryActionText={secondaryActionText}
        secondaryActionColor={secondaryActionColor}
        secondaryActionDisabled={secondaryActionDisabled}
        secondaryType={secondaryType}
        closeLabel={closeLabel}
        mobile={mobile}
        hasBorderTop={hasBorderTop}
        rightRenderOption={rightRenderOption}
        onClose={onClose}
        onCancel={onCancel}
        onConfirm={onConfirm}
        onSecondaryAction={onSecondaryAction}
      />
    </Dialog>
  )
}
